/*
 * WELLF — internal traffic flag  (STAFF TOOL, site-wide, does nothing for customers)
 * ---------------------------------------------------------------------------
 * Keeps WELLF's own browsing out of WELLF's analytics, by declining tracking consent
 * on our own devices. Measured 2026-08-27: a declined session produces ZERO GA4, zero
 * Meta and zero wellf_* hits, and the decision persists across page loads.
 *
 * Microsoft Clarity is the exception and is handled separately below. It is installed
 * as a theme APP EMBED, not a Shopify web pixel, so Shopify's consent gating never
 * touches it and it kept recording straight through a fully declined session. We stop
 * it explicitly. Do not assume declining consent covers everything.
 *
 * WHY IT EXISTS
 * One browser profile (the founder's Chrome) was 15.5% of all GA4 events, 12.6% of
 * sessions and 18.6% of pageviews across August. GA4 IP rules now catch traffic from
 * our two networks, but IP rules ONLY work for GA4: Meta has no IP exclusion at all,
 * and neither does Clarity. Consent is the only lever that covers everything, and it
 * is the only one that works off-network.
 *
 * THREE STATES, not two. A plain on/off would be useless the moment we need to verify
 * that tracking works, which is exactly the situation we were in this week.
 *
 *   ?wellf_track=off     the default for our devices. Declines consent and stops
 *                        Clarity. Nothing is recorded.
 *   ?wellf_track=debug   GRANTS consent, for deliberately testing instrumentation.
 *                        Staff never see the banner, so without this the state would be
 *                        useless to us. Typing it is an explicit request to be tracked.
 *                        Badge turns amber so it cannot be left on by accident.
 *   ?wellf_track=on      clears the flag only. It does NOT re-grant or re-decline
 *                        consent: whatever decision the browser currently holds stays.
 *                        Use debug if you want this device tracked again.
 *
 * Visit the URL once per browser. It is stored in a cookie AND localStorage for two
 * years, so it survives network changes: home, the office, mobile data, tethering.
 * That is the point, and it is why this is not just an IP rule.
 *
 * AGENTS: append ?wellf_track=off to the first navigation. Headless runs use a fresh
 * profile every time, so no cookie ever persists and the URL parameter is what does
 * the work.
 *
 * STAFF SESSIONS are declined automatically, without any flag. Shopify sets
 * sessionStorage.isMerchantSession for staff, and the founder's browser additionally
 * carries a stored ACCEPTED decision with no banner shown to revisit it, so there is
 * no way to opt out by hand.
 *
 * KNOWN LIMITATION — the visit where you SET the flag may still be counted. If this
 * browser already had consent granted, Shopify's pixels fire on page load, before the
 * Customer Privacy API finishes loading and we can decline. Every visit after that one
 * is clean, because a stored declined decision stops the pixels loading at all. Set
 * the flag, reload once, and check the badge says TRACKING OFF.
 *
 * SAFETY: for a visitor with no flag and no staff session this file returns
 * immediately, writes nothing and renders nothing.
 */
(function () {
  'use strict';

  var CS = document.currentScript;
  var DEBUG = !!(CS && CS.getAttribute('data-debug') === 'true');
  var KEY = 'wellf_track';
  var TWO_YEARS = 63072000;

  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function writeCookie(name, val) {
    try {
      document.cookie = name + '=' + encodeURIComponent(val) +
        '; max-age=' + TWO_YEARS + '; path=/; SameSite=Lax';
    } catch (e) {}
  }
  function clearCookie(name) {
    try { document.cookie = name + '=; max-age=0; path=/; SameSite=Lax'; } catch (e) {}
  }
  function ls(op, val) {
    try {
      if (op === 'get') return window.localStorage.getItem(KEY);
      if (op === 'set') return window.localStorage.setItem(KEY, val);
      if (op === 'del') return window.localStorage.removeItem(KEY);
    } catch (e) { return null; }   // private mode / storage blocked
  }

  function isMerchant() {
    try { return window.sessionStorage.getItem('isMerchantSession') === 'true'; } catch (e) { return false; }
  }

  // ── resolve state: URL wins, then stored, then staff session, else customer ──
  var param = null;
  try {
    param = new URLSearchParams(window.location.search).get(KEY);
  } catch (e) { param = null; }

  if (param === 'off' || param === 'debug') {
    writeCookie(KEY, param); ls('set', param);
  } else if (param === 'on') {
    clearCookie(KEY); ls('del');
  }

  var stored = readCookie(KEY) || ls('get');
  var state;
  if (stored === 'off' || stored === 'debug') state = stored;
  else if (param === 'on') state = 'customer';
  else if (isMerchant()) state = 'off';           // staff, implicit, never persisted
  else state = 'customer';

  if (state === 'customer') {
    if (DEBUG) { try { console.log('[wellf-track-flag] customer, no-op'); } catch (e) {} }
    return;                                        // real visitors: do nothing at all
  }

  // ── decline consent when the state is off. Explicitly overwrites an existing
  // ACCEPTED decision, which is the whole point: staff cannot reach the banner. ──
  function decline() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.setTrackingConsent !== 'function') return false;
      cp.setTrackingConsent(
        { analytics: false, marketing: false, preferences: false, sale_of_data: false },
        function () { if (DEBUG) { try { console.log('[wellf-track-flag] consent declined'); } catch (e) {} } }
      );
      return true;
    } catch (e) { return false; }
  }

  // ── grant, for the debug state. The person typing ?wellf_track=debug into their own
  // browser is explicitly asking to be tracked so they can verify instrumentation.
  // Without this the state would be useless to staff, who never see the banner. ──
  function grant() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.setTrackingConsent !== 'function') return false;
      cp.setTrackingConsent({ analytics: true, marketing: true, preferences: true, sale_of_data: true }, function () {});
      return true;
    } catch (e) { return false; }
  }

  // ── Microsoft Clarity is NOT consent-gated. Verified 2026-08-27: it kept recording
  // through a fully declined session (7 requests, including u.clarity.ms/collect),
  // because it is installed as a theme app embed rather than a Shopify web pixel, so
  // Shopify's consent gating never touches it. Consent alone therefore does NOT stop
  // it, and we have to ask it to stop directly. clarity('stop') was measured working:
  // Clarity traffic went from 7 requests to 1 immediately after calling it, and stayed
  // there across further scrolling.
  // PARTIAL, not perfect: Clarity's own script still loads before we can reach it, so a
  // session may still be registered even though the recording stops. Treat this as best
  // effort. If Clarity is not actually being used, removing the app embed is cleaner. ──
  var clarityStopped = false, clarityTries = 0;
  function stopClarity() {
    if (clarityStopped) return;
    clarityTries++;
    try {
      if (typeof window.clarity === 'function') {
        try { window.clarity('stop'); } catch (e) {}
        try { window.clarity('consent', false); } catch (e) {}
        clarityStopped = true;
        if (DEBUG) { try { console.log('[wellf-track-flag] clarity stopped'); } catch (e) {} }
        return;
      }
    } catch (e) {}
    if (clarityTries < 20) setTimeout(stopClarity, 500);   // it loads late; keep trying ~10s
  }

  if (state === 'off') {
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { decline(); });
      }
    } catch (e) {}
    setTimeout(decline, 1500);   // belt and braces if the callback never runs
    stopClarity();
  } else if (state === 'debug') {
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { grant(); });
      }
    } catch (e) {}
  }

  // ── badge. Only ever rendered for staff/flagged devices, never for customers.
  // This is not decoration: without a visible state you stop trusting the scheme,
  // and a scheme you do not trust gets bypassed and quietly rots. ──
  function badge() {
    try {
      if (document.getElementById('wellf-track-badge')) return;
      var on = (state === 'debug');
      var el = document.createElement('div');
      el.id = 'wellf-track-badge';
      el.textContent = on ? 'TRACKING ON · TEST' : 'TRACKING OFF';
      el.title = on
        ? 'This device IS being tracked, for instrumentation testing. Visit ?wellf_track=off when you are done.'
        : 'WELLF internal device: consent declined, nothing is recorded. ?wellf_track=debug to test instrumentation, ?wellf_track=on to behave as a customer.';
      el.setAttribute('style', [
        'position:fixed', 'left:10px', 'bottom:10px', 'z-index:2147483000',
        'font:600 10px/1 ui-monospace,SFMono-Regular,Menlo,monospace',
        'letter-spacing:1.2px', 'padding:6px 9px', 'border-radius:6px',
        'pointer-events:auto', 'cursor:default', 'user-select:none',
        'opacity:0.82', 'box-shadow:0 2px 8px rgba(0,0,0,.25)',
        on ? 'background:#8a5300' : 'background:#0A0A0A',
        'color:#fff', on ? 'border:1px solid #ffae00' : 'border:1px solid #444'
      ].join(';'));
      el.addEventListener('click', function () { el.style.display = 'none'; }, { passive: true });
      document.body.appendChild(el);
    } catch (e) {}
  }
  if (document.body) badge();
  else document.addEventListener('DOMContentLoaded', badge, { once: true });

  if (DEBUG) { try { console.log('[wellf-track-flag] state =', state, '| staff =', isMerchant()); } catch (e) {} }
})();
